import { createSignal, createMemo, createResource, For, Show } from 'solid-js';
import { Pagination } from '../components/Pagination';
import { DateRangePills, type DateRangeValue } from '../components/DateRangePills';
import { useParams } from '@solidjs/router';
import { useApi, isOk } from '../api';
import type { BotAuditLogEntryDto } from '../api';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import { useTablePref } from '../hooks/useTablePrefs';
import { formatDateTime } from '../services/dateFormat';
import BotManageNav from '../components/BotManageNav';
import styles from './BotAuditLog.module.css';

type AuditEntry = BotAuditLogEntryDto;

const ALL_ACTION_TYPES = [
    'Started',
    'Stopped',
    'AutoResumed',
    'Disconnected',
    'Reconnecting',
    'Reconnected',
    'ErrorState',
    'CredentialsRefreshFailed',
    'ConfigUpdated',
    'PossessionStarted',
    'PossessionReleased',
    'PossessionEvicted',
    'PossessionInterrupted',
    'CitybuildSwitchAttempted',
    'StoppedBySelfShutdown',
    'SelfShutdownSuppressed',
] as const;

const PAGE_SIZE = 30;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function badgeClass(actionType: string): string {
    switch (actionType) {
        case 'Started':                   return styles.badgeStarted;
        case 'Stopped':                   return styles.badgeStopped;
        case 'AutoResumed':               return styles.badgeAutoResumed;
        case 'Disconnected':              return styles.badgeDisconnected;
        case 'Reconnecting':              return styles.badgeReconnecting;
        case 'Reconnected':               return styles.badgeReconnected;
        case 'ErrorState':                return styles.badgeError;
        case 'CredentialsRefreshFailed':  return styles.badgeError;
        case 'ConfigUpdated':             return styles.badgeConfig;
        case 'PossessionStarted':         return styles.badgeStarted;
        case 'PossessionReleased':        return styles.badgeStopped;
        case 'PossessionEvicted':         return styles.badgeError;
        case 'PossessionInterrupted':     return styles.badgeReconnecting;
        case 'CitybuildSwitchAttempted':  return styles.badgeConfig;
        case 'StoppedBySelfShutdown':     return styles.badgeError;
        case 'SelfShutdownSuppressed':    return styles.badgeReconnecting;
        default:                          return '';
    }
}

function badgeIcon(actionType: string): string {
    switch (actionType) {
        case 'Started':                   return 'fa-solid fa-play';
        case 'Stopped':                   return 'fa-solid fa-stop';
        case 'AutoResumed':               return 'fa-solid fa-rotate';
        case 'Disconnected':              return 'fa-solid fa-plug-circle-xmark';
        case 'Reconnecting':              return 'fa-solid fa-arrows-rotate';
        case 'Reconnected':               return 'fa-solid fa-plug-circle-check';
        case 'ErrorState':                return 'fa-solid fa-triangle-exclamation';
        case 'CredentialsRefreshFailed':  return 'fa-solid fa-key';
        case 'ConfigUpdated':             return 'fa-solid fa-sliders';
        case 'PossessionStarted':         return 'fa-solid fa-ghost';
        case 'PossessionReleased':        return 'fa-solid fa-ghost';
        case 'PossessionEvicted':         return 'fa-solid fa-user-slash';
        case 'PossessionInterrupted':     return 'fa-solid fa-plug-circle-exclamation';
        case 'CitybuildSwitchAttempted':  return 'fa-solid fa-city';
        case 'StoppedBySelfShutdown':     return 'fa-solid fa-gavel';
        case 'SelfShutdownSuppressed':    return 'fa-solid fa-shield-halved';
        default:                          return 'fa-solid fa-circle';
    }
}


export default function BotAuditLog() {
    const params = useParams();
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [page, setPage] = createSignal(1);
    const [actionFilter, setActionFilter] = useTablePref<string>('botAudit.action', '');
    const [quickFilter, setQuickFilter] = useTablePref<DateRangeValue>('botAudit.range', 'all');

    const computeDateRange = (): { from?: string } => {
        const now = Date.now();
        switch (quickFilter()) {
            case 'today': return { from: new Date(now - 24 * 3600 * 1000).toISOString() };
            case '7d':    return { from: new Date(now - 7 * 24 * 3600 * 1000).toISOString() };
            case '30d':   return { from: new Date(now - 30 * 24 * 3600 * 1000).toISOString() };
            default:      return {};
        }
    };

    const [logData] = createResource(
        () => ({
            botId: params.id,
            actionType: actionFilter(),
            quickFilter: quickFilter(),
            page: page(),
        }),
        async (p): Promise<{ entries: AuditEntry[]; total: number }> => {
            if (!p.botId) return { entries: [], total: 0 };
            try {
                const range = computeDateRange();
                const res = await api.botAuditLog.getAuditLog(p.botId, {
                    page: p.page,
                    pageSize: PAGE_SIZE,
                    ...(p.actionType ? { actionType: p.actionType } : {}),
                    ...(range.from ? { from: range.from } : {}),
                });
                if (isOk(res.data)) {
                    return { entries: res.data.value.entries ?? [], total: res.data.value.total ?? 0 };
                }
            } catch { /* fall through to error toast */ }
            toast?.error(t('BOT_AUDIT_FETCH_FAILED'));
            return { entries: [], total: 0 };
        }
    );

    const entries = () => logData()?.entries ?? [];
    const total = () => logData()?.total ?? 0;
    const loading = () => logData.loading;
    const totalPages = createMemo(() => Math.max(1, Math.ceil(total() / PAGE_SIZE)));

    const applyFilters = () => setPage(1);

    const formatDetails = (json: string | null): [string, string][] => {
        if (!json) return [];
        try {
            const obj = JSON.parse(json) as Record<string, unknown>;
            const out: [string, string][] = [];
            const push = (key: string, v: unknown) => {
                if (v === null || v === undefined || v === '' || v === false) return;
                if (Array.isArray(v)) {
                    if (v.length > 0) out.push([key, v.join('\n')]);
                } else if (typeof v === 'object') {
                    for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) push(`${key}.${k2}`, v2);
                } else {
                    out.push([key, typeof v === 'string' ? v : JSON.stringify(v)]);
                }
            };
            for (const [k, v] of Object.entries(obj)) push(k, v);
            return out;
        } catch {
            return [['raw', json]];
        }
    };

    const actorLabel = (entry: AuditEntry): string => {
        if (!entry.actorUserId || entry.actorUserId === EMPTY_GUID) return t('BOT_AUDIT_ACTOR_SYSTEM');
        return entry.actorUsername ?? entry.actorUserId!;
    };

    return (
        <div class={styles.container}>
            <BotManageNav botId={params.id || ''} />

            <div class={styles.headerRow}>
                <h1><Text val="BOT_AUDIT_TITLE" /></h1>
                <span class={styles.entryCount}>
                    {total()} <Text val="BOT_AUDIT_ENTRIES" />
                </span>
            </div>

            <div class={styles.filters}>
                <select
                    value={actionFilter()}
                    onChange={(e) => { setActionFilter(e.currentTarget.value); applyFilters(); }}
                >
                    <option value="">{t('BOT_AUDIT_FILTER_ALL_ACTIONS')}</option>
                    <For each={ALL_ACTION_TYPES}>
                        {(a) => <option value={a}>{t(`BOT_AUDIT_ACTION_${a.toUpperCase()}`)}</option>}
                    </For>
                </select>

                <DateRangePills
                    value={quickFilter()}
                    onChange={(v) => { setQuickFilter(v); applyFilters(); }}
                    pills={[
                        { value: 'all',   labelKey: 'BOT_AUDIT_QUICK_ALL' },
                        { value: 'today', labelKey: 'BOT_AUDIT_QUICK_TODAY' },
                        { value: '7d',    labelKey: 'BOT_AUDIT_QUICK_7D' },
                        { value: '30d',   labelKey: 'BOT_AUDIT_QUICK_30D' },
                    ]}
                />
            </div>

            <Show when={!loading()} fallback={
                <div class={styles.logList}>
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                </div>
            }>
                <Show when={entries().length > 0} fallback={
                    <EmptyState illustration="audit" title={t('BOT_AUDIT_NO_ENTRIES')} />
                }>
                    <div class={styles.logList}>
                        <For each={entries()}>
                            {(entry, idx) => (
                                <div class={styles.logEntry} style={{ 'animation-delay': `${idx() * 0.03}s` }}>
                                    <div class={styles.logHeader}>
                                        <span class={styles.logTime}>{formatDateTime(entry.createdAt)}</span>
                                        <span class={`${styles.logBadge} ${badgeClass(entry.actionType)}`}>
                                            <i class={badgeIcon(entry.actionType)} />
                                            <Text val={`BOT_AUDIT_ACTION_${entry.actionType.toUpperCase()}`} />
                                        </span>
                                        <span class={styles.logUser}>
                                            <i class="fa-solid fa-user" style={{ 'margin-right': '4px', 'font-size': '11px', opacity: '0.5' }} />
                                            {actorLabel(entry)}
                                        </span>
                                    </div>

                                    <Show when={entry.description}>
                                        <div class={styles.logDescription}>{entry.description}</div>
                                    </Show>

                                    <Show when={entry.detailsJson && formatDetails(entry.detailsJson ?? null).length > 0}>
                                        <div class={styles.detailRows}>
                                            <For each={formatDetails(entry.detailsJson ?? null)}>
                                                {([k, v]) => (
                                                    <span class={styles.detailPair}>
                                                        <span class={styles.detailKey}>{k}</span>
                                                        <span class={styles.detailValue} style={{ 'white-space': 'pre-line' }}>{v}</span>
                                                    </span>
                                                )}
                                            </For>
                                        </div>
                                    </Show>
                                </div>
                            )}
                        </For>
                    </div>

                    <Pagination page={page() - 1} totalPages={totalPages()} onChange={(p) => setPage(p + 1)} />
                </Show>
            </Show>
        </div>
    );
}
